"use client";

import { useEffect, useRef } from "react";

const AudioPlayerWrapper = ({ htmlContent }: { htmlContent: string }) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    const initializePlayers = () => {
      const audioCards =
        containerRef.current?.querySelectorAll(".kg-audio-card");
      audioCards?.forEach((card) => {
        if (!(card as HTMLElement).dataset.initialized) {
          handleAudioPlayer(card as HTMLElement);
          (card as HTMLElement).dataset.initialized = "true";
        }
      });
    };

    const observer = new MutationObserver((mutations) => {
      mutations.forEach(() => initializePlayers());
    });

    observer.observe(containerRef.current, {
      childList: true,
      subtree: true,
    });

    initializePlayers();

    return () => observer.disconnect();
  }, [htmlContent]);

  const handleAudioPlayer = (audioElementContainer: HTMLElement) => {
    const audioPlayerContainer = audioElementContainer.querySelector(
      ".kg-audio-player-container"
    ) as HTMLElement;
    const playIconContainer = audioElementContainer.querySelector(
      ".kg-audio-play-icon"
    ) as HTMLElement;
    const pauseIconContainer = audioElementContainer.querySelector(
      ".kg-audio-pause-icon"
    ) as HTMLElement;
    const seekSlider = audioElementContainer.querySelector(
      ".kg-audio-seek-slider"
    ) as HTMLInputElement;
    const playbackRateContainer = audioElementContainer.querySelector(
      ".kg-audio-playback-rate"
    ) as HTMLElement;
    const muteIconContainer = audioElementContainer.querySelector(
      ".kg-audio-mute-icon"
    ) as HTMLElement;
    const unmuteIconContainer = audioElementContainer.querySelector(
      ".kg-audio-unmute-icon"
    ) as HTMLElement;
    const volumeSlider = audioElementContainer.querySelector(
      ".kg-audio-volume-slider"
    ) as HTMLInputElement;
    const audio = audioElementContainer.querySelector(
      "audio"
    ) as HTMLAudioElement;
    const durationContainer = audioElementContainer.querySelector(
      ".kg-audio-duration"
    ) as HTMLElement;
    const currentTimeContainer = audioElementContainer.querySelector(
      ".kg-audio-current-time"
    ) as HTMLElement;
    let raf: any = null;
    let currentPlaybackRateIdx = 1;
    let playbackRates = [
      { rate: 0.75, label: "0.7×" },
      { rate: 1.0, label: "1×" },
      { rate: 1.25, label: "1.2×" },
      { rate: 1.75, label: "1.7×" },
      { rate: 2.0, label: "2×" },
    ];

    // This code works but don't build
    //Type 'number' is not assignable to type 'string'.ts(2322)
    //Argument of type 'string' is not assignable to parameter of type 'number'.ts(2345)
    // const whilePlaying = () => {
    //   seekSlider.value = Math.floor(audio.currentTime);
    //   currentTimeContainer.textContent = calculateTime(seekSlider.value);
    //   audioPlayerContainer.style.setProperty(
    //     "--seek-before-width",
    //     `${(seekSlider.value / seekSlider.max) * 100}%`
    //   );
    //   raf = requestAnimationFrame(whilePlaying);
    // };

    // This code does not work but build
    const whilePlaying = () => {
      if (seekSlider && audio) {
        const currentValue = Math.floor(audio.currentTime);
        seekSlider.value = currentValue.toString();
        currentTimeContainer.textContent = calculateTime(currentValue);
        audioPlayerContainer.style.setProperty(
          "--seek-before-width",
          `${(currentValue / Number(seekSlider.max)) * 100}%`
        );
        raf = requestAnimationFrame(whilePlaying);
      }
    };

    const showRangeProgress = (rangeInput: HTMLInputElement) => {
      if (rangeInput === seekSlider) {
        audioPlayerContainer.style.setProperty(
          "--seek-before-width",
          (Number(rangeInput.value) / Number(rangeInput.max)) * 100 + "%"
        );
      } else {
        audioPlayerContainer.style.setProperty(
          "--volume-before-width",
          (Number(rangeInput.value) / Number(rangeInput.max)) * 100 + "%"
        );
      }
    };

    const calculateTime = (secs: number) => {
      const minutes = Math.floor(secs / 60);
      const seconds = Math.floor(secs % 60);
      const returnedSeconds = seconds < 10 ? `0${seconds}` : `${seconds}`;
      return `${minutes}:${returnedSeconds}`;
    };

    const displayDuration = () => {
      durationContainer.textContent = calculateTime(audio.duration);
    };

    const setSliderMax = () => {
      seekSlider.max = Math.floor(audio.duration).toString();
    };

    const displayBufferedAmount = () => {
      if (audio.buffered.length > 0) {
        const bufferedAmount = Math.floor(
          audio.buffered.end(audio.buffered.length - 1)
        );
        audioPlayerContainer.style.setProperty(
          "--buffered-width",
          `${(bufferedAmount / parseInt(seekSlider.max)) * 100}%`
        );
      }
    };

    if (audio.readyState > 0) {
      displayDuration();
      setSliderMax();
      displayBufferedAmount();
    } else {
      audio.addEventListener("loadedmetadata", () => {
        displayDuration();
        setSliderMax();
        displayBufferedAmount();
      });
    }

    audioElementContainer.addEventListener("click", (e) => {
      const target = e.target as Element;

      if (target.closest(".kg-audio-play-icon")) {
        audio.play();
        playIconContainer.classList.add("kg-audio-hide");
        pauseIconContainer.classList.remove("kg-audio-hide");
        requestAnimationFrame(whilePlaying);
      }

      if (target.closest(".kg-audio-pause-icon")) {
        audio.pause();
        pauseIconContainer.classList.add("kg-audio-hide");
        playIconContainer.classList.remove("kg-audio-hide");
        cancelAnimationFrame(raf);
      }

      if (target.closest(".kg-audio-mute-icon")) {
        audio.muted = false;
        muteIconContainer.classList.add("kg-audio-hide");
        unmuteIconContainer.classList.remove("kg-audio-hide");
      }

      if (target.closest(".kg-audio-unmute-icon")) {
        audio.muted = true;
        unmuteIconContainer.classList.add("kg-audio-hide");
        muteIconContainer.classList.remove("kg-audio-hide");
      }

      if (target.closest(".kg-audio-playback-rate")) {
        const nextPlaybackRate =
          playbackRates[(currentPlaybackRateIdx + 1) % 5];
        currentPlaybackRateIdx++;
        audio.playbackRate = nextPlaybackRate.rate;
        playbackRateContainer.textContent = nextPlaybackRate.label;
      }
    });

    audio.addEventListener("progress", displayBufferedAmount);

    seekSlider.addEventListener("input", (e) => {
      const target = e.target as HTMLInputElement;
      showRangeProgress(target);
      currentTimeContainer.textContent = calculateTime(parseInt(target.value));
      if (!audio.paused) {
        cancelAnimationFrame(raf);
      }
    });

    seekSlider.addEventListener("change", () => {
      audio.currentTime = parseInt(seekSlider.value);
      if (!audio.paused) {
        requestAnimationFrame(whilePlaying);
      }
    });

    volumeSlider.addEventListener("input", (e) => {
      const target = e.target as HTMLInputElement;
      const value = parseInt(target.value);
      showRangeProgress(target);
      audio.volume = value / 100;
    });
  };

  return (
    <div ref={containerRef} dangerouslySetInnerHTML={{ __html: htmlContent }} />
  );
};

export default AudioPlayerWrapper;
